/**
 * ProfileBulkUpload — customer-facing employee-roster CSV upload (B6).
 *
 * The CSV path of the locked Profile-creation flow
 * (`design/mockups/Phase3_Profile_CreationFlow.html`): download the
 * 8-column template, fill one row per employee, upload it. Each valid row
 * becomes a Lite Profile with consent_state 'csv_logged' (admin-entered,
 * recipient has not consented). Bad rows surface in a row-level error list;
 * the rest of the batch still uploads.
 *
 * Backend: POST /api/profile-batch-upload (parses via employee-upload-parser.js).
 * Templates are served from /public/ — Vite maps them to the site root.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import { Callout, SectionHeader, useToast } from "../components/index.js";
import { parseJsonResponse } from "../lib/api-fetch.js";

const TEMPLATE_CSV = "/TPM-26_Contact_Upload_Template.csv";
const TEMPLATE_XLSX = "/TPM-26_Contact_Upload_Template.xlsx";

export default function ProfileBulkUploadPage() {
  const { session } = useSession();
  const toast = useToast();

  const [fileName, setFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { created, created_profiles, invalid }

  const onFile = (e) => {
    setError(null);
    setResult(null);
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setFileName("");
      setCsvText("");
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsvText(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => setError("Couldn't read that file. Try re-saving it as a .csv.");
    reader.readAsText(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!session) {
      setError("Please sign in.");
      return;
    }
    if (!csvText.trim()) {
      setError("Choose a filled-in .csv file first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch("/.netlify/functions/profile-batch-upload", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ csv_text: csvText }),
      });
      const body = await parseJsonResponse(res);
      if (!res.ok && res.status !== 422) {
        setError(body.error || `Upload failed (HTTP ${res.status})`);
        setSubmitting(false);
        return;
      }
      // 201 success, or 422 = parsed but every row invalid. Both carry a
      // row-level breakdown the user needs to see.
      setResult({
        created: body.created || 0,
        created_profiles: body.created_profiles || [],
        invalid: body.invalid || [],
      });
      if ((body.created || 0) > 0) {
        toast.success(
          `${body.created} ${body.created === 1 ? "Profile" : "Profiles"} created.`,
          (body.invalid || []).length
            ? `${body.invalid.length} row(s) were skipped — see the list below.`
            : "Each row with a full date of birth is ready as a complete read.",
        );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px" }}>
      <SectionHeader index={1} title="Bulk upload contacts" />

      <Callout.Remember>
        Upload a list to add several people at once. Each row with a complete{" "}
        <strong>date of birth</strong> becomes a full read instantly — no invite,
        no waiting. Use the bulk upload for a longer list; a single new contact
        is faster via <Link to="/profiles/new">New Profile</Link>.
      </Callout.Remember>

      {/* ── Step 1: download the template ── */}
      <div style={panelStyle}>
        <p style={stepLabelStyle}>Step 1 — Start from the template</p>
        <p style={bodyStyle}>
          Download the template, fill one row per person, then upload it
          below. The parser expects exactly those eight columns, headers
          unchanged. Dates of birth go in <strong>MM/DD/YYYY</strong> format
          (e.g. 03/15/1988).
        </p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
          <a href={TEMPLATE_CSV} download style={downloadBtnStyle}>
            &#8615;&nbsp; Download CSV template
          </a>
          <a href={TEMPLATE_XLSX} download style={downloadBtnStyle}>
            &#8615;&nbsp; Download Excel template
          </a>
        </div>
      </div>

      {/* ── Step 2: upload ── */}
      <form onSubmit={submit} style={{ ...panelStyle, marginTop: 16 }}>
        <p style={stepLabelStyle}>Step 2 — Upload your filled-in file</p>
        <p style={bodyStyle}>
          First Name, Last Name, and Date of Birth are required on every row;
          the other columns are optional. Any row with a missing required
          field or an unreadable date is skipped and listed back to you — the
          rest of the upload still goes through.
        </p>
        <label style={fileLabelStyle}>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            style={{ display: "block" }}
            data-testid="bulk-upload-file"
          />
        </label>
        {fileName && (
          <p style={{ ...bodyStyle, marginTop: 8 }}>
            Selected: <strong>{fileName}</strong>
          </p>
        )}

        {error && <div style={errorBoxStyle}>{error}</div>}

        <button type="submit" disabled={submitting} style={submitBtnStyle(submitting)}>
          {submitting ? "Uploading…" : "Upload file"}
        </button>
      </form>

      {/* ── Result ── */}
      {result && (
        <div style={{ ...panelStyle, marginTop: 16 }} data-testid="bulk-upload-result">
          <p style={stepLabelStyle}>Upload result</p>
          <p style={bodyStyle}>
            <strong>
              {result.created} {result.created === 1 ? "Profile" : "Profiles"}{" "}
              created
            </strong>
            {result.invalid.length > 0 && (
              <> · {result.invalid.length} row(s) skipped</>
            )}
            .
          </p>
          {result.invalid.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <p style={{ ...bodyStyle, fontWeight: 600 }}>Skipped rows — fix and re-upload:</p>
              <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                {result.invalid.map((row) => (
                  <li key={row.row_number} style={invalidRowStyle}>
                    Row {row.row_number}: {row.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.created > 0 && (
            <div style={{ marginTop: 14 }}>
              <Link to="/profiles" style={linkStyle}>
                View Profiles →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const panelStyle = {
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  padding: "20px 22px",
};

const stepLabelStyle = {
  margin: "0 0 8px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-navy)",
};

const bodyStyle = {
  margin: 0,
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  lineHeight: 1.6,
  color: "var(--ppr-color-text-secondary)",
};

const downloadBtnStyle = {
  padding: "10px 16px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-accent-navy)",
  textDecoration: "none",
  borderRadius: "var(--ppr-radius-md)",
};

const fileLabelStyle = {
  display: "block",
  marginTop: 12,
  padding: "16px",
  border: "2px dashed var(--ppr-color-accent-steel)",
  background: "var(--ppr-color-accent-pale-soft)",
};

const errorBoxStyle = {
  marginTop: 12,
  padding: "12px 14px",
  border: "2px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-accent-coral-very-soft)",
  color: "var(--ppr-color-accent-coral-dark)",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
};

const invalidRowStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  lineHeight: 1.6,
  color: "var(--ppr-color-accent-coral-dark)",
};

const linkStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-navy)",
  textDecoration: "none",
};

function submitBtnStyle(submitting) {
  return {
    marginTop: 14,
    padding: "14px 18px",
    fontFamily: "var(--ppr-typography-font-family-display)",
    fontSize: "var(--ppr-typography-font-size-sm)",
    letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
    textTransform: "uppercase",
    border: "1px solid var(--ppr-color-border-default)",
    background: "var(--ppr-color-accent-navy)",
    color: "var(--ppr-color-text-inverse)",
    cursor: submitting ? "wait" : "pointer",
    opacity: submitting ? 0.7 : 1,
  };
}
